import React from 'react'
import { Virtuoso } from 'react-virtuoso'
import { Skeleton } from '@material-ui/lab'
import { TextField, Select, FormControl, FormHelperText, MenuItem } from '@material-ui/core/'

type Option = { text: string; value: string }
export type SelectBoxProps = {
  id: string
  placeholder: string
  label?: string
  disabled?: boolean
  isLoading?: boolean
  enableSearch?: boolean
  value: string | { value: string; text: string }
  onChange(value: string, text: string): void
  onOpen?: () => void
  options: Option[]
  error?: boolean
  helperText?: string
  onBlur?: () => void
}

export const SelectBox = (props: SelectBoxProps) => {
  const {
    onBlur,
    onChange,
    onOpen,
    options,
    placeholder,
    enableSearch = false,
    value: innerValue,
    error,
    helperText,
    label,
    id,
    disabled,
    isLoading,
  } = props

  const { value, text } = React.useMemo(() => {
    return typeof innerValue === 'string' ? { value: innerValue, text: innerValue } : { ...innerValue }
  }, [innerValue])

  const [open, setOpen] = React.useState<boolean>(false)
  const [searchText, setSearchText] = React.useState<string>('')

  const SearchBox = () => (
    <div className="w-full flex justify-center mb-2 px-2" key="searchBox">
      <TextField
        placeholder="Search..."
        fullWidth
        value={searchText}
        onChange={(event: any) => setSearchText(event.target.value)}
        autoFocus
      />
    </div>
  )

  const filteredOptions = React.useMemo((): Option[] => {
    // Search text with non alphanumeric characters stripped
    const strippedSearchText = searchText.toLowerCase().replace(/[^0-9a-z]/gi, '')

    return options.filter((option) => {
      return option.text
        .toLowerCase()
        .replace(/[^0-9a-z]/gi, '')
        .includes(strippedSearchText)
    })
  }, [options, searchText])

  const Items = () => {
    const optionComponents =
      filteredOptions.length > 0 ? (
        <div
          className="max-h-80 lg:max-h-96"
          style={{
            minHeight: '120px',
            height: `${Math.min(filteredOptions.length * 55, 370)}px`,
          }}
        >
          <Virtuoso
            increaseViewportBy={{ top: 0, bottom: 200 }}
            totalCount={filteredOptions.length}
            itemContent={(index: number) => {
              const item = filteredOptions[index]
              return (
                <div
                  key={item.value}
                  className={`py-4 px-3 border-b border-gray-200 cursor-pointer whitespace-pre-wrap hover:bg-primary-lighter ${
                    item.value === value ? 'bg-primary-lighter' : ''
                  }`}
                  onClick={(e) => {
                    onChange(item.value, item.text)
                    setOpen(false)
                  }}
                >
                  {item.text}
                </div>
              )
            }}
          />
        </div>
      ) : (
        <div className="p-4 text-center">No Data to Display</div>
      )

    return optionComponents
  }

  return (
    <FormControl className="flex flex-col" error={!!error} data-testid={`select-box`}>
      {label && (
        <label className={`pb-4 text-lg font-semibold ${disabled ? 'text-body-lighter' : ''}`} htmlFor={id}>
          {label}
        </label>
      )}
      <Select
        displayEmpty
        variant="outlined"
        autoWidth={false}
        disabled={disabled}
        value={value}
        open={open}
        onOpen={() => {
          setOpen(true)
          onOpen && onOpen()
        }}
        onClose={() => {
          setSearchText('')
          setOpen(false)
        }}
        renderValue={() => {
          if (value.length === 0) {
            return <span className="font-light">{placeholder}</span>
          }
          if (text === 'all') {
            return <span>All</span>
          }

          return options?.find((item) => item.value === value)?.text ?? text
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          PaperProps: {
            style: {
              wordBreak: 'break-word',
            },
          },
          getContentAnchorEl: null,
          variant: 'menu',
        }}
        style={{
          backgroundColor: 'white',
        }}
        SelectDisplayProps={{
          style: { backgroundColor: 'white' },
        }}
        onBlur={() => {
          onBlur && onBlur()
        }}
      >
        <MenuItem className="hidden" value={value}></MenuItem>
        {isLoading ? (
          <div className="gap-y-2 p-2">
            <Skeleton variant="text" animation="wave" height="50px" />
            <Skeleton variant="text" animation="wave" height="50px" />
            <Skeleton variant="text" animation="wave" height="50px" />
          </div>
        ) : options?.length ? (
          <div>
            {enableSearch && SearchBox()}
            {Items()}
          </div>
        ) : (
          <div className="p-4 text-center">No Data to Display</div>
        )}
      </Select>
      <FormHelperText className="absolute -bottom-6" error={!!error}>
        {helperText}
      </FormHelperText>
    </FormControl>
  )
}
